import { defaultRegistry } from 'react-sweet-state';

import type { BadStatusError } from '@confluence/network';

import type { FetchOptions } from './fetchResponse';
import { fetchResponse } from './fetchResponse';
import { queryStateStore } from './useExperimentalQueryState';
import { handleError } from './handleError';
import { generateCacheKey } from './generateCacheKey';

export type FetchPolicy = 'cache-first' | 'network-only' | 'cache-and-network' | 'no-cache';

type GetExperimentalQueryParams<TData, TVariables, TResponse> = FetchOptions<
	TVariables,
	TResponse
> & {
	variables: TVariables;
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
	fetchPolicy?: FetchPolicy;
	skip?: boolean;
	onError?: (error: Error | BadStatusError) => void;
};

export async function experimentalQuery<TData, TVariables, TResponse>({
	url,
	method = 'GET',
	headers,
	body,
	credentials,
	variables,
	parseResult,
	fetchPolicy = 'cache-first',
	skip = false,
	onError,
	parseResponseOnSuccess,
	parseResponseOnError,
}: GetExperimentalQueryParams<TData, TVariables, TResponse>) {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		return { data: undefined };
	}

	const key = generateCacheKey({
		url,
		method,
		headers,
		body,
		credentials,
		variables,
	});

	if (skip) {
		return { data: undefined };
	}

	if (fetchPolicy === 'cache-first') {
		const storeInstance = defaultRegistry.getStore(queryStateStore);
		const queryState = storeInstance.storeState.getState() as Record<string, any>;

		if (queryState[key]?.data !== undefined) {
			return { data: queryState[key].data };
		}
	}

	try {
		const json = await fetchResponse<TVariables, TResponse>(
			{
				url,
				method,
				body,
				headers,
				credentials,
				parseResponseOnSuccess,
				parseResponseOnError,
			},
			variables,
		);

		const data = await parseResult(json, variables);

		// Prevent writing to cache for no-cache policy, which uses local state instead
		if (fetchPolicy !== 'no-cache') {
			const storeInstance = defaultRegistry.getStore(queryStateStore);
			storeInstance.actions.setQueryState(key, {
				data,
				loading: false,
				error: undefined,
				called: true,
			});
		}

		return { data };
	} catch (error) {
		throw await handleError({ error, onError });
	}
}
